/* eslint-disable react/no-unescaped-entities */
/**
 * BookingNavbar — Header du nouveau parcours "Booking des spécialistes".
 * Sticky, glass dark, palette vert profond + accent or.
 */
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Menu, X, LogOut, LayoutDashboard } from "lucide-react";
import { useState } from "react";

const NAV = [
  { to: "/dossier", label: "Trouver mon spécialiste" },
  { to: "/verifier", label: "Vérification" },
  { to: "/negociation", label: "Négociation" },
  { to: "/about", label: "À propos" },
  { to: "/pricing", label: "Tarifs" },
];

export default function BookingNavbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  const dashLink =
    user && user.role === "admin" ? "/admin" :
    user && user.role === "expert" ? "/expert/dashboard" :
    user ? "/dashboard" : null;

  return (
    <header className="sticky top-0 z-50 cb-glass" data-testid="booking-navbar">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-20 flex items-center justify-between gap-4">
        {/* Logo officiel Confilex — image PNG (charte graphique 2026 fournie par le client) */}
        <Link to="/" className="flex items-center shrink-0 group" data-testid="booking-logo">
          <img
            src="/brand/confilex-logo-nav.png"
            alt="Confilex — Trouvez le bon spécialiste, en toute confiance"
            className="h-14 sm:h-16 w-auto group-hover:opacity-90 transition-opacity"
            data-testid="brand-logo-image"
          />
        </Link>

        {/* Nav primaire */}
        <nav className="hidden lg:flex items-center gap-9 flex-1 justify-center">
          {NAV.map((it) => {
            const active = loc.pathname === it.to;
            return (
              <Link
                key={it.to}
                to={it.to}
                data-testid={`booking-nav-${it.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className={`text-sm font-medium transition-colors ${
                  active ? "text-[#F5C400]" : "text-[#F7F7F2]/85 hover:text-[#F5C400]"
                }`}
              >
                {it.label}
              </Link>
            );
          })}
        </nav>

        {/* CTAs */}
        <div className="hidden lg:flex items-center gap-3 shrink-0">
          <Link
            to="/expert/apply"
            data-testid="booking-nav-become-expert"
            className="text-sm text-[#F7F7F2]/80 hover:text-[#F5C400] transition-colors px-3 py-2 font-medium"
          >
            Devenir spécialiste
          </Link>
          {user ? (
            <>
              {dashLink && (
                <button
                  onClick={() => nav(dashLink)}
                  className="cb-btn-gold text-sm inline-flex items-center gap-2"
                  data-testid="booking-nav-dashboard"
                >
                  <LayoutDashboard className="w-4 h-4" /> Mon espace
                </button>
              )}
              <button
                onClick={() => { logout(); nav("/"); }}
                aria-label="Déconnexion"
                className="p-2 rounded-full text-[#F7F7F2]/70 hover:text-[#F5C400] transition"
                data-testid="booking-nav-logout"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </>
          ) : (
            <button
              onClick={() => nav("/login")}
              className="cb-btn-gold text-sm"
              data-testid="booking-nav-login"
            >
              Se connecter
            </button>
          )}
        </div>

        {/* Mobile burger */}
        <button
          className="lg:hidden p-2 text-[#F7F7F2]"
          onClick={() => setOpen((o) => !o)}
          aria-label="Menu"
          data-testid="booking-mobile-menu-btn"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden cb-bg-surface border-t border-[#063D25] px-6 py-5"
             data-testid="booking-mobile-drawer">
          <div className="flex flex-col gap-1">
            {NAV.map((it) => (
              <Link
                key={it.to}
                to={it.to}
                onClick={() => setOpen(false)}
                className="text-sm py-2.5 text-[#F7F7F2]/85 hover:text-[#F5C400]"
                data-testid={`booking-mobile-nav-${it.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              >
                {it.label}
              </Link>
            ))}
            <Link
              to="/expert/apply"
              onClick={() => setOpen(false)}
              className="text-sm py-2.5 text-[#F7F7F2]/85"
            >
              Devenir spécialiste
            </Link>
            {!user ? (
              <Link
                to="/login"
                onClick={() => setOpen(false)}
                className="cb-btn-gold text-sm text-center mt-3"
                data-testid="booking-mobile-login"
              >
                Se connecter
              </Link>
            ) : dashLink && (
              <Link
                to={dashLink}
                onClick={() => setOpen(false)}
                className="cb-btn-gold text-sm text-center mt-3"
              >
                Mon espace
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
